import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { findAllMatches } from './chatSearchUtils.js';

/**
 * In-thread text search (legacy ann `useMessageSearch`).
 * @param {Array<{ content?: string }>} messages
 */
export const useChatMessageSearch = (messages) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const messageRefsRef = useRef({});

  const matches = useMemo(
    () => findAllMatches(messages, searchValue),
    [messages, searchValue],
  );

  const handleSearchToggle = useCallback(() => {
    setIsSearchOpen((prev) => !prev);
  }, []);

  const handleOpenSearch = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  const handleCloseSearch = useCallback(() => {
    setIsSearchOpen(false);
    setSearchValue('');
    setCurrentMatchIndex(0);
  }, []);

  const handleSearchChange = useCallback((valueOrEvent) => {
    const value =
      valueOrEvent?.target?.value ?? (typeof valueOrEvent === 'string' ? valueOrEvent : '');
    setSearchValue(value);
    setCurrentMatchIndex(0);
  }, []);

  const handleNextMatch = useCallback(() => {
    setCurrentMatchIndex((prev) =>
      matches.length ? Math.min(prev + 1, matches.length - 1) : 0,
    );
  }, [matches.length]);

  const handlePrevMatch = useCallback(() => {
    setCurrentMatchIndex((prev) => (matches.length ? Math.max(prev - 1, 0) : 0));
  }, [matches.length]);

  const setMessageRef = useCallback(
    (index) => (el) => {
      messageRefsRef.current[`${index}`] = el;
    },
    [],
  );

  const scrollToCurrentMatch = useCallback(() => {
    if (matches.length === 0) return;
    const current = matches[currentMatchIndex];
    const el = messageRefsRef.current[`${current.messageIndex}`];
    if (!el) return;

    const targetEl = el.querySelector('.highlight-current-js') || el;
    const scrollParent = el.closest('[data-messages-list]');
    if (!scrollParent) {
      targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const parentRect = scrollParent.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    const viewportPadding = 24;
    const isAbove = targetRect.top < parentRect.top + viewportPadding;
    const isBelow = targetRect.bottom > parentRect.bottom - viewportPadding;
    if (!isAbove && !isBelow) return;
    const maxScrollTop = scrollParent.scrollHeight - scrollParent.clientHeight;
    const targetCenter =
      targetRect.top - parentRect.top + scrollParent.scrollTop + targetRect.height / 2;
    const targetScrollTop = Math.min(
      Math.max(0, targetCenter - parentRect.height / 2),
      maxScrollTop,
    );
    scrollParent.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
  }, [matches, currentMatchIndex]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      scrollToCurrentMatch();
    });
    return () => cancelAnimationFrame(raf);
  }, [currentMatchIndex, matches.length, scrollToCurrentMatch]);

  return {
    isSearchOpen,
    searchValue,
    currentMatchIndex,
    matches,
    handleSearchToggle,
    handleOpenSearch,
    handleCloseSearch,
    handleSearchChange,
    handleNextMatch,
    handlePrevMatch,
    setMessageRef,
  };
};
